import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, TrendingUp, TrendingDown, LineChart, Handshake, Gauge, ShieldCheck, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SidebPill } from "@/components/SidebPill";
import { openChat } from "@/lib/chat";
import { Seo } from "@/components/Seo";

const STUDIES = [
  {
    slug: "rxp",
    href: "/proof/rxp",
    serviceHref: "/services/rfp-project",
    serviceLabel: "Procurement / RFP Project",
    eyebrow: "Procurement / RFP Project · PSP consolidation · Vendor selection",
    title: "Vendor consolidation that's still paying back five years on.",
    summary:
      "End-to-end RFI/RFP workstream that collapsed a fractured 4-PSP payments stack into a single converted partner — unlocking $300K+ in annual transaction-fee savings and opening two hard-to-place geographic markets the legacy partners couldn't serve.",
    kpis: [
      { value: "$300K+", label: "Annual savings", icon: TrendingUp },
      { value: "4 → 1", label: "PSP consolidation", icon: Handshake },
      { value: "5 yrs+", label: "Partnership running", icon: ArrowUpRight },
    ],
    accent: "from-[#0a1628] via-[#0a1628] to-[#2a1a5e]",
  },
  {
    slug: "aiops",
    href: "/proof/aiops",
    serviceHref: "/services/aiops-enablement",
    serviceLabel: "AI Operating Partner",
    eyebrow: "AI Operating Partner · AI rollout · BI rationalization",
    title: "Real-time margin visibility per sub-account — and a leaner BI bill.",
    summary: (
      <>
        <SidebPill asStatic /> embedded as the AI operating partner, collapsed a fragmented BI stack into one source of truth, and gave the leadership team real-time visibility into platform economics per sub-account.
      </>
    ),
    kpis: [
      { value: "$100K", label: "Tool spend eliminated", icon: TrendingUp },
      { value: "30+ hrs", label: "Reclaimed monthly", icon: Gauge },
      { value: "Real-time", label: "Margin visibility", icon: LineChart },
    ],
    accent: "from-[#1d0f3d] via-[#2a1a5e] to-[#0a1628]",
  },
  {
    slug: "studio-retainer",
    href: "/proof/studio-retainer",
    serviceHref: "/services/studio-retainer",
    serviceLabel: "Studio Retainer",
    eyebrow: "Studio Retainer · Fraud remediation · Reserve recovery",
    title: "From fraud crisis to good standing with capital back in bank.",
    summary: (
      <>
        A high-volume merchant placed into a Visa/Mastercard fraud monitoring program. <SidebPill asStatic /> ran the playbook end-to-end — fraud rate down 4x, program exited, reserve released back to the balance sheet.
      </>
    ),
    kpis: [
      { value: "2% → <0.5%", label: "Fraud rate", icon: TrendingDown },
      { value: "Exited", label: "Scheme program", icon: ShieldCheck },
      { value: "Released", label: "Reserve capital", icon: Handshake },
    ],
    accent: "from-[#0a1628] via-[#1a1346] to-[#2a1a5e]",
  },
  {
    slug: "revenue-performance",
    href: "/proof/revenue-performance",
    serviceHref: "/services/enablement-sprint",
    serviceLabel: "Payments Optimization Sprint",
    eyebrow: "Payments Optimization Sprint · MSB · Auth lift · AFT/OCT",
    title: "Acceptance economics rebuilt — and revenue recovered.",
    summary: (
      <>
        A regulated global Money Services Business running high-volume cross-border remittance. <SidebPill asStatic /> re-engineered API orchestration, locked AFT/OCT compliance, and unified acceptance economics — 30% off vendor spend, +3% acceptance, and a multi-year LTV flywheel underneath.
      </>
    ),
    kpis: [
      { value: "30%", label: "Vendor cost cut", icon: TrendingUp },
      { value: "+$18M", label: "Recovered volume", icon: TrendingUp },
      { value: "1.5x", label: "LTV flywheel", icon: TrendingUp },
    ],
    accent: "from-[#2a1a5e] via-[#0a1628] to-[#1d0f3d]",
  },
];

export default function Proof() {
  return (
    <div className="bg-white" data-testid="proof-hub-page">
      <Seo
        title="Proof · SideB Case Studies — Payments, AIOps, and Revenue Performance"
        description="Named case studies from SideB engagements: PSP consolidation ($300K+ savings), AI Operating Partner ($100K BI spend eliminated), Studio Retainer (Visa/Mastercard fraud program exit), and Payments Optimization Sprint (30% cost cut + 1.5% acceptance lift, ~$9M recovered)."
        keywords="SideB case studies, PSP consolidation case study, AI Operating Partner outcomes, payments optimization sprint results, Visa EFM exit case, fraud remediation results"
        canonical="https://sideb.io/proof"
        schema={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "SideB Proof · Case Studies",
          url: "https://sideb.io/proof",
          description:
            "Named case studies from SideB engagements — PSP consolidation, AI Operating Partner, Studio Retainer, and Payments Optimization Sprint.",
          publisher: { "@type": "Organization", name: "SideB", url: "https://sideb.io" },
          hasPart: [
            { "@type": "Article", url: "https://sideb.io/proof/rxp", headline: "PSP Consolidation — $300K+ annual savings" },
            { "@type": "Article", url: "https://sideb.io/proof/aiops", headline: "AI Operating Partner — $100K BI spend eliminated" },
            { "@type": "Article", url: "https://sideb.io/proof/studio-retainer", headline: "Studio Retainer — Visa/Mastercard fraud program exit" },
            { "@type": "Article", url: "https://sideb.io/proof/revenue-performance", headline: "Payments Optimization Sprint — 30% cost cut + 1.5% acceptance lift" },
          ],
        }}
      />
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          aria-hidden
          style={{
            background:
              "radial-gradient(60% 50% at 75% 30%, rgba(172,98,234,1) 0%, rgba(11,30,90,0) 70%)",
            filter: "blur(40px)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <div className="label-mono">Proof · Selected case studies</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 tracking-[-0.035em] leading-[1.05] font-semibold max-w-4xl">
            Consulting that paid back. <span className="text-[#ac62ea]">In dollars and scaling value.</span>
          </h1>
          <p className="text-lg text-muted-foreground mt-6 max-w-2xl leading-relaxed">
            From fintech to revenue operations, see how we&apos;ve helped digital commerce leaders unlock hidden revenue. Dive into what the client was working through, how we helped, and the value that landed.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3" data-testid="proof-hub-hero-ctas">
            <Button
              asChild
              className="btn-pill bg-[#ac62ea] hover:bg-[#8a48cf] text-white h-11 px-6"
              data-testid="proof-hub-hero-read-cta"
            >
              <a href="#case-studies">Read case studies <ArrowRight className="ml-2 h-4 w-4" /></a>
            </Button>
            <button
              type="button"
              onClick={() => openChat({ context: "Proof hub · Hero CTA" })}
              className="btn-pill h-11 px-6 rounded-full border border-border bg-white hover:bg-muted text-sm font-medium"
              data-testid="proof-hub-hero-chat-cta"
            >
              Chat with us &rarr;
            </button>
          </div>
        </div>
      </section>

      {/* What you'd expect — industry-agnostic outcome card */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-12 lg:pt-16" data-testid="proof-hub-outcome-card">
        <div className="card-sharp p-6 lg:p-8 border-[#ac62ea]/30 bg-gradient-to-br from-white via-white to-[#f3eefb]/40">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[#ac62ea]" />
            <span className="label-mono text-[#7d3fbf]">What you&apos;d expect on any <SidebPill /> engagement</span>
          </div>
          <h2 className="font-display text-2xl lg:text-3xl mt-3 tracking-tight font-semibold leading-snug">
            Different industries. Same outcome vectors. <span className="text-[#ac62ea]">All measured.</span>
          </h2>
          <p className="text-sm text-muted-foreground mt-3 max-w-3xl leading-relaxed">
            The cases on this page are representative of a wider body of work across payments, fintech, and revenue operations. Every engagement gets measured against the vectors below.
          </p>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="rounded-xl border border-border bg-white p-4">
              <div className="w-9 h-9 rounded-lg bg-[#ac62ea]/12 flex items-center justify-center">
                <TrendingUp className="h-4 w-4 text-[#ac62ea]" />
              </div>
              <div className="font-display text-base mt-3 font-semibold tracking-tight">Hard-dollar savings</div>
              <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">Eliminated tool spend, renegotiated vendor cost, removed redundant fees.</p>
            </div>
            <div className="rounded-xl border border-border bg-white p-4">
              <div className="w-9 h-9 rounded-lg bg-[#ac62ea]/12 flex items-center justify-center">
                <LineChart className="h-4 w-4 text-[#ac62ea]" />
              </div>
              <div className="font-display text-base mt-3 font-semibold tracking-tight">Recovered revenue</div>
              <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">Acceptance lift, opened markets, customers that come back and transact again.</p>
            </div>
            <div className="rounded-xl border border-border bg-white p-4">
              <div className="w-9 h-9 rounded-lg bg-[#ac62ea]/12 flex items-center justify-center">
                <ShieldCheck className="h-4 w-4 text-[#ac62ea]" />
              </div>
              <div className="font-display text-base mt-3 font-semibold tracking-tight">Risk neutralized</div>
              <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">Scheme programs exited, compliance aligned, capital unfrozen and back in the business.</p>
            </div>
            <div className="rounded-xl border border-border bg-white p-4">
              <div className="w-9 h-9 rounded-lg bg-[#ac62ea]/12 flex items-center justify-center">
                <Gauge className="h-4 w-4 text-[#ac62ea]" />
              </div>
              <div className="font-display text-base mt-3 font-semibold tracking-tight">Faster decisions</div>
              <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">Real-time visibility replacing monthly lag, with the data model and rituals that stay.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Studies grid */}
      <section id="case-studies" className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20 scroll-mt-24">
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
          {STUDIES.map((s) => (
            <Link
              key={s.slug}
              to={s.href}
              className="group relative overflow-hidden rounded-2xl border border-border bg-white transition-all duration-300 hover:border-[#ac62ea]/40 hover:shadow-[0_24px_60px_-20px_rgba(11,30,90,0.25)] hover:-translate-y-0.5"
              data-testid={`proof-card-${s.slug}`}
            >
              {/* Top dark band with KPIs */}
              <div className={`relative bg-gradient-to-br ${s.accent} text-white p-6 lg:p-8`}>
                <div
                  className="absolute inset-0 opacity-30 pointer-events-none"
                  aria-hidden
                  style={{
                    background:
                      "radial-gradient(50% 50% at 80% 30%, rgba(172,98,234,0.55) 0%, rgba(11,30,90,0) 70%)",
                    filter: "blur(40px)",
                  }}
                />
                <div className="relative">
                  <div className="label-mono text-white/70">{s.eyebrow}</div>
                  <div className="mt-5 grid grid-cols-3 gap-3">
                    {s.kpis.map((k) => {
                      const Icon = k.icon;
                      return (
                        <div key={k.label} className="rounded-xl border border-white/10 bg-white/[0.04] p-3">
                          <Icon className="h-3.5 w-3.5 text-[#c9a3f4]" />
                          <div className="font-display text-xl lg:text-2xl mt-2 font-semibold tracking-tight">{k.value}</div>
                          <div className="text-[10px] uppercase tracking-widest text-white/55 mt-1 leading-snug">{k.label}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="p-6 lg:p-8">
                <h2 className="font-display text-2xl lg:text-3xl tracking-tight font-semibold leading-snug group-hover:text-[#ac62ea] transition-colors" data-testid={`proof-card-title-${s.slug}`}>
                  {s.title}
                </h2>
                <p className="mt-4 text-muted-foreground leading-relaxed">{s.summary}</p>
                <div className="mt-6 flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground/60">
                    Engagement: <span className="text-[#ac62ea]">{s.serviceLabel}</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#ac62ea]">
                    Read the case study <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Closing CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24" data-testid="proof-hub-cta">
        <div className="relative overflow-hidden rounded-2xl border border-border bg-[#f3eefb] p-8 lg:p-12">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <div className="label-mono">Looking for something custom?</div>
              <h2 className="font-display text-3xl sm:text-4xl mt-3 tracking-[-0.03em] leading-[1.1] font-semibold">
                These are a sample of our portfolio. <span className="text-[#ac62ea]">Yours could be next.</span>
              </h2>
              <p className="text-muted-foreground mt-4 max-w-xl leading-relaxed">
                More engagements like these — and beyond payments — have shipped under NDA. Send a brief, or open a chat — we&apos;ll get your project scoped and recommend the best way to engage. Your <SidebPill /> case study is waiting.
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-3">
              <Button asChild className="btn-pill bg-[#ac62ea] hover:bg-[#8a48cf] text-white h-11 px-6 w-full sm:w-auto" data-testid="proof-hub-brief">
                <Link to="/inquiry">Send a brief <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <button
                type="button"
                onClick={() => openChat({ context: "Proof hub · Custom inquiry" })}
                className="btn-pill h-11 px-6 rounded-full border border-border bg-white hover:bg-muted text-sm font-medium"
                data-testid="proof-hub-chat"
              >
                Or chat with us &rarr;
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
